import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sendRequest } from '../../utils/dateHelpers';
import Loading from '../Loading';

const Settings = () => {
  const { userInfo } = useSelector(state => state);
  const user = userInfo?.user || {};
  const displayName =
    user?.fullName ||
    user?.full_name ||
    user?.displayName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    '';
  const companyId = user?.companyId || '';
  const [mcpConnections, setMcpConnections] = useState([]);
  const [mcpLoading, setMcpLoading] = useState(true);

  const loadMcpConnections = useCallback(async () => {
    try {
      const response = await sendRequest('/oauth/mcp/connections');
      setMcpConnections(
        response?.connections || response?.data?.connections || []
      );
    } catch {
      setMcpConnections([]);
    } finally {
      setMcpLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMcpConnections();
  }, [loadMcpConnections]);

  const disconnectMcpClient = async clientId => {
    await sendRequest(
      `/oauth/mcp/connections/${encodeURIComponent(clientId)}`,
      'DELETE'
    );
    setMcpConnections(current =>
      current.filter(connection => connection.clientId !== clientId)
    );
  };

  return (
    <div className="mx-auto max-w-270 space-y-6">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="p-5 lg:p-6">
          <div className="flex flex-col gap-6">
            <div>
              <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5 dark:border-strokedark dark:from-white/5 dark:to-transparent lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-lg font-semibold uppercase text-white shadow-lg shadow-blue-600/20">
                    {(displayName || 'G').charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                      Personal Information
                    </h4>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Your account and company identity details used across
                      GitMe.
                    </p>
                  </div>
                </div>

                <div className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-200">
                  {user?.companyName || 'No company assigned'}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                  <p className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Name
                  </p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    {displayName || '-'}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                  <p className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Email address
                  </p>
                  <p className="break-all text-sm font-semibold text-gray-800 dark:text-white/90">
                    {user?.email || '-'}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                  <p className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Company
                  </p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    {user?.companyName || '-'}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-900/40">
                  <p className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                    Company ID
                  </p>
                  <p className="break-all text-sm font-semibold text-gray-800 dark:text-white/90">
                    {companyId || '-'}
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 dark:border-strokedark dark:bg-boxdark">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-base font-semibold text-gray-800 dark:text-white/90">
                      Connected MCP Apps
                    </h4>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Apps such as Codex or ChatGPT that can access GitMe using
                      your role permissions.
                    </p>
                  </div>
                  <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                    MCP
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  {mcpLoading ? (
                    <Loading type="csv" />
                  ) : mcpConnections.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No MCP apps are connected to this account.
                    </p>
                  ) : (
                    mcpConnections.map(connection => (
                      <div
                        key={connection.clientId}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 dark:border-strokedark"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-white">
                            {connection.clientName}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {(connection.scopes || []).join(', ') ||
                              'gitme.read'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            disconnectMcpClient(connection.clientId)
                          }
                          className="rounded border border-danger/30 px-2.5 py-1 text-xs font-medium text-danger hover:bg-danger/5"
                        >
                          Disconnect
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
